import axios from "axios";
import { financeBaseUrl, getOperationsAccessToken, renewOperationsAccessToken } from "../../../api/config";
import { OwnStaffPayMonth, StaffPayHours } from "../types";

type StaffPayResponseType = "json" | "blob";

export const STAFF_PAY_QUERY_KEY = "staff-pay";

const STAFF_PAY_FALLBACK_ERROR_MESSAGE = "Não foi possível carregar os valores.";
const STAFF_PAY_MAXIMUM_RETRIES = 2;
const STAFF_PAY_REQUEST_TIMEOUT_MILLISECONDS = 20 * 1000;

const postToStaffPay = async (
    path: string,
    requestFields: Record<string, unknown>,
    operationsToken: string | null,
    responseType: StaffPayResponseType
) => {
    const response = await axios.post(
        `${financeBaseUrl}/staff-pay${path}`,
        { ...requestFields, operations_token: operationsToken },
        { responseType, timeout: STAFF_PAY_REQUEST_TIMEOUT_MILLISECONDS }
    );
    return response;
};

const readResponseStatus = (error: unknown) => {
    const responseStatus = axios.isAxiosError(error) ? error.response?.status : undefined;
    return responseStatus;
};

const postWithOperationsToken = async (path: string, requestFields: Record<string, unknown>, responseType: StaffPayResponseType = "json") => {
    const operationsToken = await getOperationsAccessToken();
    try {
        const response = await postToStaffPay(path, requestFields, operationsToken, responseType);
        return response;
    } catch (error) {
        if (readResponseStatus(error) !== 401 || !operationsToken) {
            throw error;
        }
        const renewedToken = await renewOperationsAccessToken(operationsToken);
        const retriedResponse = await postToStaffPay(path, requestFields, renewedToken, responseType);
        return retriedResponse;
    }
};

type StaffPayHoursRequest = { userIds: Array<string>; fromYear: number; fromMonth: number; year: number; month: number };

export const fetchStaffPayHours = async ({ userIds, fromYear, fromMonth, year, month }: StaffPayHoursRequest) => {
    const response = await postWithOperationsToken("/hours", {
        user_ids: userIds,
        from_year: fromYear,
        from_month: fromMonth,
        year,
        month,
    });
    const staffPayHours: StaffPayHours = response.data.data;
    return staffPayHours;
};

export const fetchOwnStaffPayMonth = async ({ year, month }: { year: number; month: number }) => {
    const response = await postWithOperationsToken("/month", { year, month });
    const ownStaffPayMonth: OwnStaffPayMonth = response.data.data;
    return ownStaffPayMonth;
};

export const fetchOwnPayslip = async (fileId: string) => {
    const response = await postWithOperationsToken("/file", { file_id: fileId }, "blob");
    const payslipBlob: Blob = response.data;
    return payslipBlob;
};

export const readStaffPayErrorMessage = (error: unknown) => {
    const serverMessage = axios.isAxiosError(error) ? error.response?.data?.errors?.[0]?.message : undefined;
    const errorMessage = typeof serverMessage === "string" ? serverMessage : STAFF_PAY_FALLBACK_ERROR_MESSAGE;
    return errorMessage;
};

export const shouldRetryStaffPayRequest = (failureCount: number, error: unknown) => {
    const responseStatus = readResponseStatus(error);
    const isRefusal = responseStatus !== undefined && responseStatus >= 400 && responseStatus < 500;
    const shouldRetry = !isRefusal && failureCount < STAFF_PAY_MAXIMUM_RETRIES;
    return shouldRetry;
};
